import React, { useState, useEffect, useRef } from "react";

const ResizableTable = ({
  data,
  columns,
  visibleColumns,
  renderCell,
  renderHeader, // Add renderHeader prop
  getRowStyle,
}) => {
  // Enhanced state for better column management
  const [columnWidths, setColumnWidths] = useState({});
  const [resizing, setResizing] = useState(null);
  const [resizeLine, setResizeLine] = useState({ visible: false, position: 0 });
  const [touchStartX, setTouchStartX] = useState(null);
  const [isMobile, setIsMobile] = useState(false);
  const tableRef = useRef(null);
  const tableWrapperRef = useRef(null);

  // Check if we're on a mobile device
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    // Set initial value
    checkMobile();

    // Add event listener for window resize
    window.addEventListener("resize", checkMobile);

    // Cleanup
    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  // Load saved column widths from localStorage
  useEffect(() => {
    try {
      const savedWidths = localStorage.getItem("createvista_column_widths");
      if (savedWidths) {
        const parsedWidths = JSON.parse(savedWidths);
        // Only use saved widths for columns that still exist
        const validWidths = {};
        columns.forEach((column) => {
          if (parsedWidths[column.id]) {
            validWidths[column.id] = parsedWidths[column.id];
          } else {
            // Default width if not saved
            validWidths[column.id] = column.width || (isMobile ? 200 : 150);
          }
        });
        setColumnWidths(validWidths);
      } else {
        // Initialize with defaults if no saved data
        const initialWidths = {};
        columns.forEach((column) => {
          // Special case for actions columns - make them wider
          if (column.id === "actions" || column.id === "actions2") {
            initialWidths[column.id] = column.width || 220;
          } else {
            initialWidths[column.id] = column.width || (isMobile ? 200 : 150);
          }
        });
        setColumnWidths(initialWidths);
      }
    } catch (error) {
      console.error("Error loading saved column widths:", error);
      // Fallback to defaults
      const initialWidths = {};
      columns.forEach((column) => {
        initialWidths[column.id] = column.width || (isMobile ? 200 : 150);
      });
      setColumnWidths(initialWidths);
    }
  }, [columns, isMobile]);

  // Save column widths to localStorage when they change
  useEffect(() => {
    if (Object.keys(columnWidths).length > 0) {
      localStorage.setItem(
        "createvista_column_widths",
        JSON.stringify(columnWidths)
      );
    }
  }, [columnWidths]);

  // Reset column widths to defaults
  const resetColumnWidths = () => {
    const defaultWidths = {};
    columns.forEach((column) => {
      defaultWidths[column.id] = column.width || (isMobile ? 200 : 150);
    });
    setColumnWidths(defaultWidths);
    localStorage.removeItem("createvista_column_widths");
  };

  // Double-click to auto-size column
  const handleDoubleClick = (columnId) => {
    // In a real implementation, this would measure content and set optimal width
    // For now, we'll just set a reasonable default
    setColumnWidths((prev) => ({
      ...prev,
      [columnId]: 150,
    }));
  };

  // Enhanced mouse event handlers
  const handleMouseDown = (e, columnId) => {
    e.preventDefault();
    const tableRect = tableWrapperRef.current.getBoundingClientRect();
    const startPosition = e.clientX - tableRect.left;

    setResizing({
      columnId,
      startX: e.pageX,
      startWidth: columnWidths[columnId],
    });

    setResizeLine({
      visible: true,
      position: startPosition,
    });
  };

  const handleMouseMove = (e) => {
    if (!resizing) return;

    // Update resize line position for visual feedback
    if (tableWrapperRef.current) {
      const tableRect = tableWrapperRef.current.getBoundingClientRect();
      const newPosition = e.clientX - tableRect.left;
      setResizeLine({
        visible: true,
        position: newPosition,
      });
    }

    const deltaX = e.pageX - resizing.startX;
    const newWidth = Math.max(80, resizing.startWidth + deltaX);
    setColumnWidths((prev) => ({
      ...prev,
      [resizing.columnId]: newWidth,
    }));
  };

  const handleMouseUp = () => {
    setResizing(null);
    setResizeLine({ visible: false, position: 0 });
  };

  // Touch event handlers
  const handleTouchStart = (e, columnId) => {
    const touch = e.touches[0];
    setTouchStartX(touch.pageX);
    setResizing({
      columnId,
      startX: touch.pageX,
      startWidth: columnWidths[columnId],
    });
  };

  const handleTouchMove = (e) => {
    if (!resizing || !touchStartX) return;
    const touch = e.touches[0];
    const deltaX = touch.pageX - resizing.startX;
    const newWidth = Math.max(50, resizing.startWidth + deltaX);
    setColumnWidths((prev) => ({
      ...prev,
      [resizing.columnId]: newWidth,
    }));
  };

  const handleTouchEnd = () => {
    setResizing(null);
    setTouchStartX(null);
  };

  useEffect(() => {
    if (resizing) {
      document.addEventListener("mousemove", handleMouseMove);
      document.addEventListener("mouseup", handleMouseUp);
      document.addEventListener("touchmove", handleTouchMove);
      document.addEventListener("touchend", handleTouchEnd);
      return () => {
        document.removeEventListener("mousemove", handleMouseMove);
        document.removeEventListener("mouseup", handleMouseUp);
        document.removeEventListener("touchmove", handleTouchMove);
        document.removeEventListener("touchend", handleTouchEnd);
      };
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resizing]);

  const tableStyles = {
    container: {
      position: "relative",
      height: isMobile ? "auto" : "calc(100vh - 350px)",
      maxHeight: isMobile ? "80vh" : "calc(100vh - 350px)",
      width: "100%",
    },
    wrapper: {
      position: isMobile ? "relative" : "absolute",
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      overflowX: "auto",
      overflowY: "auto",
      WebkitOverflowScrolling: "touch", // Smooth scrolling on iOS
      boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
    },
    resizeLine: {
      position: "absolute",
      top: 0,
      bottom: 0,
      width: "2px",
      backgroundColor: "#3b82f6",
      zIndex: 10,
      opacity: 0.8,
      pointerEvents: "none",
    },
    resetButton: {
      position: "absolute",
      top: "-30px",
      right: "10px",
      padding: "4px 8px",
      fontSize: "12px",
      backgroundColor: "#e5e7eb",
      color: "#4b5563",
      border: "none",
      borderRadius: "4px",
      cursor: "pointer",
      zIndex: 4,
    },
    table: {
      width: "auto",
      minWidth: "100%",
      backgroundColor: "white",
      borderCollapse: "separate",
      borderSpacing: 0,
      borderRadius: "8px",
    },
    th: {
      position: "sticky",
      top: 0,
      backgroundColor: "#f8fafc",
      zIndex: 2,
      padding: "16px",
      borderBottom: "2px solid #e2e8f0",
      color: "#4b5563",
      fontSize: isMobile ? "14px" : "12px",
      fontWeight: "600",
      textTransform: "uppercase",
      textAlign: "left",
      whiteSpace: "nowrap",
      boxShadow: "0 2px 3px rgba(0,0,0,0.05)",
    },
    td: {
      padding: isMobile ? "8px 12px" : "6px 12px",
      borderBottom: "1px solid #e2e8f0",
      whiteSpace: isMobile ? "normal" : "nowrap",
      overflow: "hidden",
      textOverflow: "ellipsis",
      fontSize: isMobile ? "13px" : "inherit",
      lineHeight: "1.2",
    },
    resizeHandle: {
      position: "absolute",
      right: 0,
      top: "20%",
      height: "60%", // Cover only middle portion for better visibility
      width: "12px", // Wider touch target
      background: "linear-gradient(to right, transparent, #e2e8f0)",
      cursor: "col-resize",
      zIndex: 3,
      touchAction: "none", // Prevents scrolling while resizing
      borderRadius: "2px",
      "&:hover": {
        background: "linear-gradient(to right, transparent, #3b82f6)",
      },
      "&:active": {
        background: "linear-gradient(to right, transparent, #2563eb)",
      },
    },
    resizeHandleIcon: {
      position: "absolute",
      right: "3px",
      top: "50%",
      transform: "translateY(-50%)",
      width: "2px",
      height: "16px",
      backgroundColor: "#9ca3af",
      borderRadius: "1px",
      "&:before": {
        content: '""',
        position: "absolute",
        left: "-3px",
        width: "2px",
        height: "16px",
        backgroundColor: "#9ca3af",
        borderRadius: "1px",
      },
    },
  };

  return (
    <div style={tableStyles.container} ref={tableRef}>
      <button
        style={tableStyles.resetButton}
        onClick={resetColumnWidths}
        title="Reset column widths to default"
      >
        Reset Columns
      </button>
      <div style={tableStyles.wrapper} ref={tableWrapperRef}>
        {resizeLine.visible && (
          <div
            style={{
              ...tableStyles.resizeLine,
              left: `${resizeLine.position}px`,
            }}
          />
        )}
        <table style={tableStyles.table}>
          <thead>
            <tr>
              {columns.map(
                (column) =>
                  visibleColumns[column.id] && (
                    <th
                      key={column.id}
                      style={{
                        ...tableStyles.th,
                        width: columnWidths[column.id],
                        minWidth: columnWidths[column.id],
                      }}
                    >
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                          position: "relative",
                        }}
                      >
                        {/* Render header content */}
                        {renderHeader ? renderHeader(column) : column.label}
                        {/* Resize handle with visual indicator */}
                        <div
                          style={tableStyles.resizeHandle}
                          onMouseDown={(e) => handleMouseDown(e, column.id)}
                          onTouchStart={(e) => handleTouchStart(e, column.id)}
                          onDoubleClick={() => handleDoubleClick(column.id)}
                          title="Drag to resize (double-click to auto-size)"
                        >
                          <div style={tableStyles.resizeHandleIcon} />
                        </div>
                      </div>
                    </th>
                  )
              )}
            </tr>
          </thead>
          <tbody>
            {data.map((row, rowIndex) => (
              <tr
                key={rowIndex}
                style={{
                  ...getRowStyle?.(row),
                  transition: "background-color 0.2s",
                  "&:hover": {
                    backgroundColor: "rgba(243, 244, 246, 0.8)",
                  },
                  ...(rowIndex % 2 === 1
                    ? { backgroundColor: "rgba(249, 250, 251, 0.5)" }
                    : {}),
                }}
              >
                {columns.map(
                  (column) =>
                    visibleColumns[column.id] && (
                      <td
                        key={column.id}
                        style={{
                          ...tableStyles.td,
                          width: columnWidths[column.id],
                          maxWidth: columnWidths[column.id],
                        }}
                      >
                        {renderCell(row, column.id)}
                      </td>
                    )
                )}
              </tr>
            ))}
            {data.length === 0 && (
              <tr>
                <td
                  colSpan={
                    columns.filter((col) => visibleColumns[col.id]).length
                  }
                  style={{
                    ...tableStyles.td,
                    textAlign: "center",
                    padding: "32px 16px",
                    color: "#6b7280",
                  }}
                >
                  No data found
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default ResizableTable;
